import { useEffect, useState } from 'react'
import { useSearchParams, useNavigate, Link } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { supabase } from '@/lib/supabase'
import { callEdgeFunction } from '@/lib/edge-functions'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { CheckCircle, XCircle, Loader2 } from 'lucide-react'
import { getPageTitle } from '@/lib/branding'

type VerificationStatus = 'verifying' | 'success' | 'error' | 'expired'

export function ConfirmPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [status, setStatus] = useState<VerificationStatus>('verifying')
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [countdown, setCountdown] = useState(3)

  const tokenHash = searchParams.get('token_hash')
  const type = searchParams.get('type') as 'signup' | 'recovery' | 'email_change' | 'magiclink' | null

  useEffect(() => {
    async function verifyEmail() {
      if (!tokenHash || !type) {
        setStatus('error')
        setErrorMessage('Invalid confirmation link. Please request a new one.')
        return
      }

      try {
        const { data, error } = await supabase.auth.verifyOtp({
          token_hash: tokenHash,
          type: type,
        })

        if (error) {
          console.error('Verification error:', error)
          if (error.message.includes('expired') || error.message.includes('invalid')) {
            setStatus('expired')
            setErrorMessage('This confirmation link has expired. Please request a new one.')
          } else {
            setStatus('error')
            setErrorMessage(error.message)
          }
          return
        }

        if (data.session) {
          setStatus('success')

          // Create customer profile if this is a signup
          if (type === 'signup' && data.user) {
            try {
              await supabase
                .from('customer_profiles')
                .upsert(
                  {
                    id: data.user.id,
                    email: data.user.email!,
                    display_name: data.user.user_metadata?.display_name || null,
                  },
                  { onConflict: 'id' }
                )
            } catch {
              // Non-critical, continue
            }

            // Claim any guest orders
            try {
              await callEdgeFunction('claim-orders', {
                method: 'POST',
                withAuth: true,
              })
            } catch {
              // Non-critical, continue
            }
          }
        } else {
          // No session returned - might be a different flow
          setStatus('success')
        }
      } catch (err) {
        console.error('Unexpected error:', err)
        setStatus('error')
        setErrorMessage('An unexpected error occurred. Please try again.')
      }
    }

    verifyEmail()
  }, [tokenHash, type])

  // Countdown on success
  useEffect(() => {
    if (status !== 'success') return

    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer)
          return 0
        }
        return prev - 1
      })
    }, 1000)

    return () => clearInterval(timer)
  }, [status])

  // Redirect when countdown reaches 0
  useEffect(() => {
    if (status === 'success' && countdown === 0) {
      if (type === 'recovery') {
        navigate('/auth/reset-password', { replace: true })
      } else {
        navigate('/account', { replace: true })
      }
    }
  }, [status, countdown, type, navigate])

  const handleResendEmail = () => {
    navigate('/?auth=signup', { replace: true })
  }

  return (
    <>
      <Helmet>
        <title>{getPageTitle('Confirm Email')}</title>
      </Helmet>
      <div className="min-h-screen flex items-center justify-center bg-muted/30 p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl">
              {status === 'verifying' && 'Verifying your email...'}
              {status === 'success' && 'Email Confirmed!'}
              {status === 'error' && 'Verification Failed'}
              {status === 'expired' && 'Link Expired'}
            </CardTitle>
            <CardDescription>
              {status === 'verifying' && 'Please wait while we confirm your email address.'}
              {status === 'success' && 'Your email has been verified successfully.'}
              {status === 'error' && 'We could not verify your email address.'}
              {status === 'expired' && 'This confirmation link is no longer valid.'}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {status === 'verifying' && (
              <div className="flex flex-col items-center justify-center py-8">
                <Loader2 className="h-12 w-12 animate-spin text-muted-foreground" />
                <p className="mt-4 text-sm text-muted-foreground">
                  Confirming your email address...
                </p>
              </div>
            )}

            {status === 'success' && (
              <div className="flex flex-col items-center justify-center py-8">
                <div className="rounded-full bg-green-100 p-3">
                  <CheckCircle className="h-12 w-12 text-green-600" />
                </div>
                <p className="mt-4 text-sm text-muted-foreground">
                  Redirecting to your account in {countdown} seconds...
                </p>
                <Button
                  className="mt-4"
                  onClick={() => navigate(type === 'recovery' ? '/auth/reset-password' : '/account', { replace: true })}
                >
                  Continue Now
                </Button>
              </div>
            )}

            {(status === 'error' || status === 'expired') && (
              <div className="flex flex-col items-center justify-center py-4">
                <div className="rounded-full bg-red-100 p-3">
                  <XCircle className="h-12 w-12 text-red-600" />
                </div>

                {errorMessage && (
                  <Alert variant="destructive" className="mt-4">
                    <AlertDescription>{errorMessage}</AlertDescription>
                  </Alert>
                )}

                <div className="mt-6 space-y-3 w-full">
                  {type === 'signup' && (
                    <Button
                      variant="outline"
                      className="w-full"
                      onClick={handleResendEmail}
                    >
                      Request New Confirmation Email
                    </Button>
                  )}
                  <Button asChild className="w-full">
                    <Link to="/?auth=signin">Back to Sign In</Link>
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </>
  )
}
